import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, X, Sparkles, ChevronRight } from "lucide-react";
import { toast } from "sonner";

/**
 * WelcomeChecklistCard — R115 resident-side.
 *
 * Renders a 5-task first-week checklist. Auto-hides once every task is
 * done or dismissed. Each task links straight to the page that
 * completes it. A resident can dismiss individual tasks they don't
 * apply to (e.g., no car).
 */
export default function WelcomeChecklistCard() {
  const [state, setState] = useState(null);
  const [dismissing, setDismissing] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/onboarding-checklist");
      setState(data);
    } catch {
      setState({ items: [], pct_complete: 0, all_complete: true });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dismiss = async (taskId) => {
    setDismissing(taskId);
    try {
      await api.post(`/me/onboarding-checklist/${taskId}/dismiss`);
      toast.success("Task skipped");
      load();
    } catch {
      toast.error("Failed to skip");
    } finally { setDismissing(null); }
  };

  if (!state) return null;
  if (state.all_complete) return null;

  const pct = state.pct_complete || 0;

  return (
    <Card className="p-5 sm:p-6 bg-gradient-to-br from-emerald-50 to-white border-emerald-100 relative overflow-hidden"
          data-testid="welcome-checklist-card">
      <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="relative">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-600" />
            <div className="overline text-emerald-800">Welcome — first week checklist</div>
          </div>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="text-xs text-emerald-700 hover:text-emerald-900"
            data-testid="checklist-toggle-collapse"
          >
            {collapsed ? "Show" : "Hide"}
          </button>
        </div>
        <div className="text-xl sm:text-2xl font-semibold font-display text-zinc-900 mt-1">
          {pct >= 60 ? "You're almost home." : "Let's get you set up."}
        </div>
        <div className="text-sm text-zinc-600 mt-1">
          {state.counted} of {state.total} complete · <span className="tabular-nums font-medium">{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-emerald-100 mt-3 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all"
               style={{ width: `${pct}%` }}
               data-testid="checklist-progress-bar" />
        </div>

        {!collapsed && (
          <div className="mt-5 space-y-2" data-testid="checklist-tasks">
            {state.items.map((t) => {
              const done = t.done;
              const skipped = t.dismissed && !done;
              return (
                <div key={t.id}
                     className={"rounded-lg border p-3 flex items-start gap-3 " +
                       (done ? "bg-emerald-50/60 border-emerald-200"
                             : skipped ? "bg-zinc-50 border-zinc-200 opacity-70"
                                       : "bg-white border-zinc-200 hover:border-emerald-300 transition")}
                     data-testid={`checklist-task-${t.id}`}>
                  {done ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                  ) : (
                    <Circle className={"h-5 w-5 shrink-0 mt-0.5 " + (skipped ? "text-zinc-300" : "text-zinc-400")} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={"font-medium text-sm " + (done ? "line-through text-zinc-500" : skipped ? "text-zinc-400" : "text-zinc-900")}>
                      {t.title}
                    </div>
                    <div className={"text-xs mt-0.5 " + (done || skipped ? "text-zinc-400" : "text-zinc-600")}>
                      {skipped ? "Skipped" : t.description}
                    </div>
                  </div>
                  {!done && !skipped && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-8"
                        onClick={() => nav(t.href)}
                        data-testid={`checklist-cta-${t.id}`}
                      >
                        {t.cta} <ChevronRight className="h-3 w-3 ml-0.5" />
                      </Button>
                      <button
                        onClick={() => dismiss(t.id)}
                        disabled={dismissing === t.id}
                        title="Skip this task"
                        className="p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600"
                        data-testid={`checklist-dismiss-${t.id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}
